"use client";

import { useAppHomeHref } from "@/hooks/use-app-home-href";
import { useWorkspaceNavigation } from "@/hooks/use-workspace-navigation";
import { collection } from "@/lib/collections";
import { routes } from "@/lib/navigation/routes";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { IconGithub, IconPlus, IconTerminal } from "@teral/icons";
import { Crumb } from "./crumb";
import type { CrumbPopoverItem } from "./crumb-popover";

export function AppCrumb({ projectId, appId }: { projectId: string; appId: string }) {
  const workspace = useWorkspaceNavigation();
  const appHomeHref = useAppHomeHref();
  const appsQuery = useLiveQuery(
    (q) =>
      q
        .from({ app: collection.apps })
        .where(({ app }) => eq(app.projectId, projectId))
        .orderBy(({ app }) => app.updatedAt, { direction: "desc", nulls: "last" })
        .orderBy(({ app }) => app.id, "desc"),
    [projectId],
  );
  const apps = appsQuery.data ?? [];
  const current = apps.find((a) => a.id === appId);

  const items: CrumbPopoverItem[] = apps.map((a) => ({
    id: a.id,
    label: a.name,
    href: appHomeHref({
      workspaceSlug: workspace.slug,
      projectId,
      appId: a.id,
    }),
  }));

  return (
    <Crumb
      icon={
        current?.repositoryFullName ? (
          <IconGithub className="size-3.5 text-gray-11" />
        ) : (
          <IconTerminal className="size-3.5 text-gray-11" />
        )
      }
      label={current?.name ?? appId}
      loading={appsQuery.isLoading}
      href={appHomeHref({ workspaceSlug: workspace.slug, projectId, appId })}
      items={items}
      currentId={appId}
      searchPlaceholder="Find app..."
      emptyText="No apps found"
      footer={{
        icon: IconPlus,
        label: "New app",
        href: routes.projects.apps.new({ workspaceSlug: workspace.slug, projectId }),
      }}
    />
  );
}
